/**
 * PlanLimitUpsell — turns "invite failed" into "upgrade to invite".
 *
 * Hitting the plan's user cap is not an error, it is the moment to sell the
 * next plan. Used by the dashboard invite card, the Fyrstu skrefin checklist
 * and the Teymi settings page so the message is identical everywhere.
 *
 * Bookkeeper invites never count against the cap (backend: check_user_limit),
 * so callers must only treat counting roles as capped.
 */
import React from 'react';
import { ArrowUpRight } from 'lucide-react';

export const NEXT_PLAN = { solo: 'team', team: 'business' };
export const PLAN_LABEL = { solo: 'Einstaklingur', team: 'Teymið', business: 'Stærri fyrirtæki' };
export const PLAN_MAX_USERS = { solo: 1, team: 5, business: 15 };

/** Roles that consume a seat. */
export const COUNTS_AGAINST_LIMIT = (role) => role !== 'bookkeeper';

/** True when a 403 from the invite endpoint is the user-cap, not something else. */
export const isUserLimitError = (err) => {
  const d = err?.response?.data?.detail;
  // The server now answers with a structured body; older builds sent a string.
  if (d && typeof d === 'object') return d.code === 'plan_user_limit';
  return typeof d === 'string' && /leyfir .* notend|notanda/i.test(d);
};

export const upsellText = (plan, maxUsers, lang) => {
  const next = NEXT_PLAN[plan];
  const current = PLAN_LABEL[plan] || plan;
  const max = maxUsers || PLAN_MAX_USERS[plan] || 1;
  if (!next) {
    return lang === 'is'
      ? `${current} leyfir ${max} notendur. Hafðu samband til að fá fleiri.`
      : `${current} allows ${max} users. Contact us to add more.`;
  }
  const nextLabel = PLAN_LABEL[next];
  if (lang === 'is') {
    const users = max === 1 ? '1 notanda' : `${max} notendur`;
    const why = plan === 'solo' ? 'til að bjóða starfsmönnum' : 'til að bjóða fleiri';
    return `${current} leyfir ${users}. Uppfærðu í ${nextLabel} ${why}.`;
  }
  return `${current} allows ${max === 1 ? '1 user' : `${max} users`}. Upgrade to ${nextLabel} to invite ${plan === 'solo' ? 'employees' : 'more people'}.`;
};

const PlanLimitUpsell = ({ plan = 'solo', maxUsers, lang = 'is', onUpgrade, testId = 'plan-limit-upsell' }) => {
  const next = NEXT_PLAN[plan];
  return (
    <div
      className="rounded-lg border border-accent/30 bg-accent/5 p-3 flex items-start gap-3"
      data-testid={testId}
    >
      <p className="text-xs text-foreground flex-1 min-w-0" data-testid={`${testId}-text`}>
        {upsellText(plan, maxUsers, lang)}
      </p>
      {next && (
        <button
          type="button"
          onClick={() => onUpgrade(next)}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-accent-foreground text-xs font-semibold hover:bg-accent/90 transition-colors"
          data-testid={`${testId}-upgrade`}
        >
          <ArrowUpRight size={13} />
          {lang === 'is' ? 'Uppfæra' : 'Upgrade'}
        </button>
      )}
    </div>
  );
};

export default PlanLimitUpsell;
